import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { currentLocale } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { getMedalImageUrl, getImageBasePath } from '../../utils/formatters';
import ImageLightbox from '../common/ImageLightbox';
import './AdminMedals.css';

// Module-level so they stay stable; values are translation keys resolved
// via t() in the component (reusing the leagues/medals groups).
const PLACEMENT_KEYS = { 1: 'leagues.placeGold', 2: 'leagues.placeSilver', 3: 'leagues.placeBronze' };

const CATEGORY_KEYS = {
  milestones: 'medals.catMilestones',
  combat: 'medals.catCombat',
  skill: 'medals.catSkill',
  utility: 'medals.catUtility',
};

/**
 * List of hardcoded achievement IDs for reference image picker.
 * Matches the PNG files in /images/achievements/.
 */
const ACHIEVEMENT_IDS = [
  'ace', 'ace_collector', 'adr_beast', 'assist_king', 'clutch_king',
  'clutch_legend', 'clutch_master', 'double_trouble', 'entry_fragger',
  'entry_master', 'first_blood', 'flash_god', 'flash_master', 'grinder',
  'hand_century', 'headhunter', 'kast_star', 'kd_positive', 'killer',
  'legion', 'map_explorer', 'map_veteran', 'multikill_machine',
  'sharpshooter', 'survivor', 'team_player', 'ten_matches', 'terminator',
  'thousand_kills', 'utility_expert', 'utility_lord', 'veteran',
];

/**
 * Admin page for managing tournament medals, generating free-form images,
 * and creating custom achievements.
 * @param {Object} props
 * @param {Object} props.perms - Admin permissions object.
 */
export default function AdminMedals({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('gallery');
  // Resolve a placement to a localized label, falling back to "#N".
  const placementLabel = (p) => (PLACEMENT_KEYS[p] ? t(PLACEMENT_KEYS[p]) : `#${p}`);

  // Gallery state
  const [medals, setMedals] = useState([]);
  const [medalsLoading, setMedalsLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Generator state
  const [genPrompt, setGenPrompt] = useState('');
  const [genFilename, setGenFilename] = useState('');
  const [genRefSource, setGenRefSource] = useState('none');
  const [genRefId, setGenRefId] = useState(null);
  const [genTarget, setGenTarget] = useState('medals');
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [genError, setGenError] = useState('');

  // Custom achievements state
  const [customAch, setCustomAch] = useState([]);
  const [availableStats, setAvailableStats] = useState(null);
  const [achForm, setAchForm] = useState({
    id: '', name: '', description: '', category: 'milestones',
    stat: 'kills', operator: '>=', threshold: '', icon: '',
  });
  const [achError, setAchError] = useState('');
  const [achSuccess, setAchSuccess] = useState('');

  const fetchMedals = useCallback(async () => {
    setMedalsLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/medals/all`, {
        headers: getAuthHeaders(),
      });
      if (resp.ok) setMedals(await resp.json());
    } catch (e) { /* silent */ }
    setMedalsLoading(false);
  }, [getAuthHeaders]);

  // Bragderna bor i Django sedan motorn flyttades dit. Endpointen kräver
  // achievements.manage och returnerar ALLA definitioner — grundbragderna
  // filtreras bort här eftersom fliken bara hanterar egna.
  const fetchCustomAchievements = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/achievements/definitions`, {
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        const data = await resp.json();
        setCustomAch((data.definitions || []).filter(d => !d.is_builtin));
      }
    } catch (e) { /* silent */ }
  }, [getAuthHeaders]);

  const fetchAvailableStats = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/achievements/available-stats`);
      if (resp.ok) {
        const data = await resp.json();
        // Django svarar {stats: [{key, label}], ...}; UI:t vill ha {key: label}.
        setAvailableStats(
          Object.fromEntries((data.stats || []).map(s => [s.key, s.label]))
        );
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchMedals();
    fetchCustomAchievements();
    fetchAvailableStats();
  }, [fetchMedals, fetchCustomAchievements, fetchAvailableStats]);

  /**
   * Delete a medal by ID, with file cleanup on backend.
   * @param {string} medalId - Medal ID to delete.
   */
  const handleDeleteMedal = async (medalId) => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/medals/${medalId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (resp.ok || resp.status === 204) {
        setMedals((prev) => prev.filter((m) => m.id !== medalId));
        setConfirmDelete(null);
      }
    } catch (e) { /* silent */ }
  };

  /**
   * Fetch an image as base64 for use as reference.
   * @param {string} url - Image URL to fetch.
   * @returns {Promise<string>} Base64 encoded image data.
   */
  const fetchImageAsBase64 = async (url) => {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const b64 = reader.result.split(',')[1];
        resolve(b64);
      };
      reader.readAsDataURL(blob);
    });
  };

  /**
   * Generate a free-form image via the API.
   */
  const handleGenerate = async () => {
    if (!genPrompt.trim()) return;
    setGenerating(true);
    setGenError('');
    setGenResult(null);

    try {
      const body = { prompt: genPrompt.trim(), target: genTarget };
      if (genFilename.trim()) body.filename = genFilename.trim();

      if (genRefSource === 'medal' && genRefId) {
        const medal = medals.find((m) => m.id === genRefId);
        if (medal) {
          body.reference_base64 = await fetchImageAsBase64(getMedalImageUrl(medal.image_url));
        }
      } else if (genRefSource === 'achievement' && genRefId) {
        const url = `${getImageBasePath()}/images/achievements/${genRefId}.png`;
        body.reference_base64 = await fetchImageAsBase64(url);
      }

      const resp = await fetch(`${API_URL}/api/tournaments/medals/generate-image`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (resp.ok) {
        const data = await resp.json();
        setGenResult(data);
        fetchMedals();
      } else {
        const err = await resp.json();
        setGenError(err.error || t('admin.genFailed'));
      }
    } catch (e) {
      setGenError(t('settings.networkError'));
    }
    setGenerating(false);
  };

  /**
   * Create a custom achievement via the API.
   */
  const handleCreateAchievement = async () => {
    setAchError('');
    setAchSuccess('');
    if (!achForm.id || !achForm.name || !achForm.description || !achForm.threshold) {
      setAchError(t('admin.fillRequired'));
      return;
    }
    try {
      const resp = await fetch(`${API_URL}/api/achievements/definitions`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        // Django-modellen kallar nyckeln `code` (id är prefix-pk:n).
        body: JSON.stringify({ ...achForm, code: achForm.id }),
      });
      if (resp.ok) {
        setAchSuccess(t('admin.achCreated', { name: achForm.name }));
        setAchForm({ id: '', name: '', description: '', category: 'milestones', stat: 'kills', operator: '>=', threshold: '', icon: '' });
        fetchCustomAchievements();
      } else {
        const err = await resp.json();
        setAchError(err.error || err.detail || t('admin.createFailed'));
      }
    } catch (e) {
      setAchError(t('settings.networkError'));
    }
  };

  /**
   * Delete a custom achievement by ID.
   * @param {string} achId - Achievement ID to delete.
   */
  const handleDeleteAchievement = async (achId) => {
    try {
      const resp = await fetch(`${API_URL}/api/achievements/definitions/${achId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        fetchCustomAchievements();
      } else {
        const err = await resp.json();
        setAchError(err.error || err.detail || t('admin.createFailed'));
      }
    } catch (e) {
      setAchError(t('settings.networkError'));
    }
  };

  return (
    <div>
      <h1 className="admin-section-title">{t('admin.navMedals')}</h1>

      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'gallery' ? 'active' : ''}`} onClick={() => setTab('gallery')}>
          {t('admin.medalGallery')}
        </button>
        <button className={`admin-tab ${tab === 'generator' ? 'active' : ''}`} onClick={() => setTab('generator')}>
          {t('admin.imageGeneration')}
        </button>
        <button className={`admin-tab ${tab === 'achievements' ? 'active' : ''}`} onClick={() => setTab('achievements')}>
          {t('admin.customAchievements')}
        </button>
      </div>

      {/* Medal Gallery */}
      {tab === 'gallery' && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.allMedals', { count: medals.length })}</div>
          {medalsLoading ? (
            <div className="admin-info">{t('live.loading')}</div>
          ) : medals.length === 0 ? (
            <div className="admin-info">{t('admin.noMedals')}</div>
          ) : (
            <div className="medal-admin-grid">
              {medals.map((m) => (
                <div key={m.id} className="medal-admin-card">
                  <img
                    src={getMedalImageUrl(m.image_url)}
                    alt={`${m.tournament_name} #${m.placement}`}
                    className="medal-admin-img clickable"
                    onClick={() => setLightbox({
                      src: getMedalImageUrl(m.image_url),
                      title: `${m.tournament_name} — ${placementLabel(m.placement)}`,
                      subtitle: m.display_name || '',
                    })}
                  />
                  <div className="medal-admin-info">
                    <span className="medal-admin-tournament">{m.tournament_name}</span>
                    <span className="medal-admin-tier">{placementLabel(m.placement)}</span>
                    {m.display_name && <span className="medal-admin-player">{m.display_name}</span>}
                    <span className="medal-admin-date">{new Date(m.created_at).toLocaleDateString(currentLocale())}</span>
                  </div>
                  <button
                    className="medal-admin-delete"
                    title={t('admin.deleteMedal')}
                    onClick={() => setConfirmDelete(m)}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Free-form Generator */}
      {tab === 'generator' && (
        <div className="admin-card">
          <div className="admin-card-title">{t('admin.genImageTitle')}</div>
          <div className="admin-card-desc">
            {t('admin.genImageDesc')}
          </div>

          <div className="admin-form-group">
            <label className="tournament-label">{t('admin.promptLabel')}</label>
            <textarea
              className="admin-input gen-prompt-input"
              value={genPrompt}
              onChange={(e) => setGenPrompt(e.target.value)}
              placeholder={t('admin.promptPlaceholder')}
              rows={4}
            />
          </div>

          <div className="admin-form-group">
            <label className="tournament-label">{t('admin.saveAs')}</label>
            <div className="gen-ref-tabs">
              <button
                className={`tournament-chip ${genTarget === 'medals' ? 'active' : ''}`}
                onClick={() => setGenTarget('medals')}
              >
                {t('admin.medal')}
              </button>
              <button
                className={`tournament-chip ${genTarget === 'achievements' ? 'active' : ''}`}
                onClick={() => setGenTarget('achievements')}
              >
                {t('admin.achievementIcon')}
              </button>
            </div>
          </div>

          <div className="admin-form-group">
            <label className="tournament-label">{t('admin.filenameLabel')}</label>
            <input
              className="admin-input"
              value={genFilename}
              onChange={(e) => setGenFilename(e.target.value)}
              placeholder={genTarget === 'achievements' ? t('admin.filenamePlaceholderAch') : t('admin.filenamePlaceholderAuto')}
            />
          </div>

          <div className="admin-form-group">
            <label className="tournament-label">{t('admin.referenceImage')}</label>
            <div className="gen-ref-tabs">
              <button
                className={`tournament-chip ${genRefSource === 'none' ? 'active' : ''}`}
                onClick={() => { setGenRefSource('none'); setGenRefId(null); }}
              >
                {t('admin.none')}
              </button>
              <button
                className={`tournament-chip ${genRefSource === 'medal' ? 'active' : ''}`}
                onClick={() => { setGenRefSource('medal'); setGenRefId(null); }}
              >
                {t('admin.medal')}
              </button>
              <button
                className={`tournament-chip ${genRefSource === 'achievement' ? 'active' : ''}`}
                onClick={() => { setGenRefSource('achievement'); setGenRefId(null); }}
              >
                {t('admin.achievement')}
              </button>
            </div>

            {genRefSource === 'medal' && (
              <div className="gen-ref-picker">
                {medals.length === 0 ? (
                  <div className="admin-info">{t('admin.noMedalsRef')}</div>
                ) : (
                  medals.map((m) => (
                    <img
                      key={m.id}
                      src={getMedalImageUrl(m.image_url)}
                      alt={m.tournament_name}
                      className={`gen-ref-thumb ${genRefId === m.id ? 'selected' : ''}`}
                      onClick={() => setGenRefId(m.id)}
                    />
                  ))
                )}
              </div>
            )}

            {genRefSource === 'achievement' && (
              <div className="gen-ref-picker">
                {ACHIEVEMENT_IDS.map((id) => (
                  <img
                    key={id}
                    src={`${getImageBasePath()}/images/achievements/${id}.png`}
                    alt={id}
                    className={`gen-ref-thumb ${genRefId === id ? 'selected' : ''}`}
                    onClick={() => setGenRefId(id)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="admin-row">
            <button
              className="admin-btn"
              onClick={handleGenerate}
              disabled={generating || !genPrompt.trim()}
            >
              {generating ? t('admin.generating') : t('admin.generateImage')}
            </button>
          </div>

          {genError && <div className="admin-error">{genError}</div>}

          {genResult && (
            <div className="gen-result">
              <img
                src={getMedalImageUrl(genResult.image_url)}
                alt="Generated"
                className="gen-result-img clickable"
                onClick={() => setLightbox({
                  src: getMedalImageUrl(genResult.image_url),
                  title: t('admin.generatedImage'),
                  subtitle: genResult.filename,
                })}
              />
              <div className="gen-result-info">
                <span className="admin-success">{t('admin.imageGenerated')}</span>
                <span className="admin-info">{genResult.filename}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Custom Achievements */}
      {tab === 'achievements' && (
        <>
          <div className="admin-card">
            <div className="admin-card-title">{t('admin.existingAchievements', { count: customAch.length })}</div>
            {customAch.length === 0 ? (
              <div className="admin-info">{t('admin.noAchievements')}</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{t('admin.nameLabel')}</th>
                    <th>{t('admin.category')}</th>
                    <th>{t('admin.condition')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {customAch.map((a) => (
                    <tr key={a.id}>
                      <td>{a.id}</td>
                      <td>{a.icon} {a.name}</td>
                      <td>{CATEGORY_KEYS[a.category] ? t(CATEGORY_KEYS[a.category]) : a.category}</td>
                      <td>{a.stat} {a.operator} {a.threshold}</td>
                      <td>
                        <button
                          className="admin-btn admin-btn-danger"
                          onClick={() => handleDeleteAchievement(a.id)}
                        >
                          {t('admin.delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="admin-card">
            <div className="admin-card-title">{t('admin.createNewAchievement')}</div>
            <div className="admin-card-desc">
              {t('admin.createAchDesc')}
            </div>

            <div className="ach-form-grid">
              <div className="admin-form-group">
                <label className="tournament-label">{t('admin.idLabel')}</label>
                <input
                  className="admin-input"
                  value={achForm.id}
                  onChange={(e) => setAchForm({ ...achForm, id: e.target.value })}
                  placeholder={t('admin.achIdPlaceholder')}
                />
              </div>

              <div className="admin-form-group">
                <label className="tournament-label">{t('admin.nameLabel')}</label>
                <input
                  className="admin-input"
                  value={achForm.name}
                  onChange={(e) => setAchForm({ ...achForm, name: e.target.value })}
                  placeholder={t('admin.achNamePlaceholder')}
                />
              </div>

              <div className="admin-form-group">
                <label className="tournament-label">{t('leagues.description')}</label>
                <input
                  className="admin-input"
                  value={achForm.description}
                  onChange={(e) => setAchForm({ ...achForm, description: e.target.value })}
                  placeholder={t('admin.achDescPlaceholder')}
                />
              </div>

              <div className="admin-form-group">
                <label className="tournament-label">{t('admin.iconLabel')}</label>
                <input
                  className="admin-input"
                  value={achForm.icon}
                  onChange={(e) => setAchForm({ ...achForm, icon: e.target.value })}
                  placeholder={t('admin.achIconPlaceholder')}
                />
              </div>

              <div className="admin-form-group">
                <label className="tournament-label">{t('admin.category')}</label>
                <select
                  className="admin-input"
                  value={achForm.category}
                  onChange={(e) => setAchForm({ ...achForm, category: e.target.value })}
                >
                  {Object.entries(CATEGORY_KEYS).map(([k, keyPath]) => (
                    <option key={k} value={k}>{t(keyPath)}</option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label className="tournament-label">{t('admin.statLabel')}</label>
                <select
                  className="admin-input"
                  value={achForm.stat}
                  onChange={(e) => setAchForm({ ...achForm, stat: e.target.value })}
                >
                  {availableStats && Object.entries(availableStats.stats).map(([k, v]) => (
                    <option key={k} value={k}>{v} ({k})</option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label className="tournament-label">{t('admin.operatorLabel')}</label>
                <select
                  className="admin-input"
                  value={achForm.operator}
                  onChange={(e) => setAchForm({ ...achForm, operator: e.target.value })}
                >
                  {['>=', '<=', '==', '>', '<'].map((op) => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label className="tournament-label">{t('admin.threshold')}</label>
                <input
                  className="admin-input"
                  type="number"
                  step="any"
                  value={achForm.threshold}
                  onChange={(e) => setAchForm({ ...achForm, threshold: e.target.value })}
                  placeholder={t('admin.achThresholdPlaceholder')}
                />
              </div>
            </div>

            <div className="admin-row ach-form-actions">
              <button
                className="admin-btn"
                onClick={handleCreateAchievement}
                disabled={!achForm.id || !achForm.name || !achForm.threshold}
              >
                {t('admin.createAchievement')}
              </button>
              {achForm.id && (
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={() => {
                    setGenFilename(`${achForm.id}.png`);
                    setGenTarget('achievements');
                    setTab('generator');
                  }}
                >
                  {t('admin.generateIcon')}
                </button>
              )}
            </div>

            {achError && <div className="admin-error">{achError}</div>}
            {achSuccess && <div className="admin-success">{achSuccess}</div>}
          </div>
        </>
      )}

      {/* Lightbox */}
      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          title={lightbox.title}
          subtitle={lightbox.subtitle}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="admin-confirm-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="admin-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>{t('admin.deleteMedalConfirm')}</h3>
            <p>
              {confirmDelete.tournament_name} — {placementLabel(confirmDelete.placement)}
              {confirmDelete.display_name ? ` (${confirmDelete.display_name})` : ''}
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setConfirmDelete(null)}>
                {t('matchDetail.cancel')}
              </button>
              <button className="admin-btn admin-btn-danger" onClick={() => handleDeleteMedal(confirmDelete.id)}>
                {t('admin.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
